/**
 * Sources found — the documents the source finder found on the web for plans
 * with no source yet. Nothing reads them until a person accepts one:
 *
 *   Accept & draft  registers it as the plan's source and queues the rules
 *                   builder for that plan now (its drafts then wait in Review);
 *   Accept          registers it; the next scheduled builder run reads it;
 *   Reject          needs a reason; that URL is never suggested again for the plan.
 *
 * Open each document before accepting: the finder checks that it is readable,
 * names the plan and reads like a billing document, not that it is current.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Check, ExternalLink, Play, RefreshCw, Search, X } from 'lucide-react';
import { PROGRAM_TYPES, funderAgentsApi, type ProgramType, type SourceSuggestion } from '../../../shared/services/funderAgentsApi';
import { Btn, Empty, ErrorLine, NoteForm, errText, fmtDate, type Notify } from './shared';

export default function SuggestionsView({
  notify,
  onDecided,
  onRunStarted,
}: {
  notify: Notify;
  onDecided?: () => void;
  onRunStarted?: () => void;
}) {
  const [items, setItems] = useState<SourceSuggestion[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [rejecting, setRejecting] = useState<number | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [programs, setPrograms] = useState<Record<number, ProgramType>>({});

  const load = useCallback(async () => {
    setError(null);
    try {
      setItems(await funderAgentsApi.suggestions({ status: 'pending' }));
    } catch (e) {
      setError(errText(e));
      setItems([]);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const groups = useMemo(() => {
    const m = new Map<number, SourceSuggestion[]>();
    (items ?? []).forEach((s) => m.set(s.payer_id, [...(m.get(s.payer_id) ?? []), s]));
    return Array.from(m.values());
  }, [items]);

  const done = (id: number, text: string) => {
    setItems((list) => (list ?? []).filter((s) => s.id !== id));
    notify(true, text);
    onDecided?.();
  };

  const accept = async (s: SourceSuggestion, draft: boolean) => {
    const program = programs[s.id] ?? s.program_type;
    setBusy(`${draft ? 'draft' : 'accept'}-${s.id}`);
    try {
      const out = await funderAgentsApi.acceptSuggestion(s.id, { program_type: program, draft });
      const run = out?.run;
      done(
        s.id,
        draft && run
          ? run.status === 'failed'
            ? `Source added for ${s.payer_name}, but the draft run could not be queued; the next scheduled run reads it.`
            : `Source added for ${s.payer_name}; rules builder run #${run.id} queued. Its drafts will wait in Review.`
          : `Source added for ${s.payer_name}. The rules builder reads it on its next run.`,
      );
      if (draft) onRunStarted?.();
    } catch (e) {
      notify(false, errText(e));
    } finally {
      setBusy(null);
    }
  };

  const reject = async (s: SourceSuggestion, note: string) => {
    try {
      await funderAgentsApi.rejectSuggestion(s.id, note);
      setRejecting(null);
      done(s.id, `Rejected for ${s.payer_name}; it will not be suggested again.`);
    } catch (e) {
      notify(false, errText(e));
    }
  };

  const findNow = async () => {
    if (
      !window.confirm(
        'Search the web now for plans with no source?\n\nUp to 5 plans not searched in the last 30 days. What it finds lands here for a person to accept.',
      )
    )
      return;
    setBusy('find');
    try {
      const run = await funderAgentsApi.startRun({ agent: 'finder' });
      notify(true, `Source finder run #${run?.id ?? ''} queued. Results land here when it finishes.`);
      onRunStarted?.();
    } catch (e) {
      notify(false, errText(e));
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm text-ink-500">
          {items ? `${items.length} document${items.length === 1 ? '' : 's'} found, waiting for a person.` : 'Loading…'} Open each one before you accept it.
        </p>
        <span className="flex gap-2">
          <Btn busy={busy === 'find'} onClick={() => void findNow()}>
            <Search className="w-3.5 h-3.5" aria-hidden="true" />
            Find sources now
          </Btn>
          <Btn tone="ghost" title="Refresh" onClick={() => void load()}>
            <RefreshCw className="w-3.5 h-3.5" aria-hidden="true" />
          </Btn>
        </span>
      </div>
      <ErrorLine text={error} />
      {items !== null && items.length === 0 && !error && (
        <Empty>
          Nothing waiting. The source finder searches each morning for plans with no source; run it now, or pick plans in Plans and use Find
          sources for selected.
        </Empty>
      )}
      {groups.map((list) => (
        <section key={list[0].payer_id} className="rounded-md border border-v2border bg-surface">
          <h4 className="px-3 py-2 border-b border-v2border text-sm font-semibold text-ink">
            {list[0].payer_name}{' '}
            <span className="font-normal text-ink-500">
              · {list.length} document{list.length === 1 ? '' : 's'} found
            </span>
          </h4>
          <ul className="divide-y divide-v2border">
            {list.map((s) => (
              <li key={s.id} className="p-3 space-y-2">
                <div className="flex flex-wrap items-center gap-2 text-sm">
                  <a
                    href={s.source_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 font-semibold text-v2accent-ink hover:underline"
                  >
                    {s.title || 'Untitled document'} <ExternalLink className="w-3 h-3" aria-hidden="true" />
                  </a>
                  <span className="text-xs text-ink-500">Found {fmtDate(s.created_at)}</span>
                </div>
                <p className="break-all font-mono text-xs text-ink-500">{s.source_url}</p>
                {s.reason && <p className="text-sm text-ink-700">{s.reason}</p>}
                <div className="flex flex-wrap items-center gap-2">
                  <label className="text-xs text-ink-700">
                    <span className="sr-only">Program for {s.title || s.source_url}</span>
                    <select
                      value={programs[s.id] ?? s.program_type}
                      onChange={(e) => setPrograms((m) => ({ ...m, [s.id]: e.target.value as ProgramType }))}
                      className="rounded-md border border-v2border bg-surface px-2 py-1.5 text-sm text-ink"
                    >
                      {PROGRAM_TYPES.map((p) => (
                        <option key={p} value={p}>
                          {p === 'ALL' ? 'All programs' : p}
                        </option>
                      ))}
                    </select>
                  </label>
                  <Btn tone="primary" busy={busy === `draft-${s.id}`} disabled={busy !== null} onClick={() => void accept(s, true)}>
                    <Play className="w-3.5 h-3.5" aria-hidden="true" />
                    Accept &amp; draft rules
                  </Btn>
                  <Btn busy={busy === `accept-${s.id}`} disabled={busy !== null} onClick={() => void accept(s, false)}>
                    <Check className="w-3.5 h-3.5" aria-hidden="true" />
                    Accept
                  </Btn>
                  <Btn tone="danger" onClick={() => setRejecting(rejecting === s.id ? null : s.id)}>
                    <X className="w-3.5 h-3.5" aria-hidden="true" />
                    Reject
                  </Btn>
                </div>
                {rejecting === s.id && (
                  <NoteForm
                    label="Why reject it? (kept on record; this URL will not be suggested again)"
                    placeholder="e.g. Member handbook, not the provider manual"
                    submitLabel="Reject document"
                    onSubmit={(note) => reject(s, note)}
                    onCancel={() => setRejecting(null)}
                  />
                )}
              </li>
            ))}
          </ul>
        </section>
      ))}
    </div>
  );
}
